import { useState, useRef, useEffect, useCallback } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { X, Camera } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import Cropper from "react-easy-crop";
import api from "../../http";
import { appConfig } from "@/appConfig";
import { useColor } from "@/contexts/ColorContext";

interface ProfileImageModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentUser: {
        id: string;
        first_name?: string | null;
        last_name?: string | null;
        email: string;
        profile_image_url?: string | null;
    } | null;
    onProfileImageUpdate: (imageUrl: string) => void;
    onUserInfoUpdate: (updatedUser: {
        id: string;
        first_name?: string | null;
        last_name?: string | null;
        email: string;
        profile_image_url?: string | null;
    }) => void;
}

// Helper function to create image cropping
const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', error => reject(error));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });

const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: { x: number; y: number; width: number; height: number }
): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('No 2d context');
    }

    // Set canvas size to crop size
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped image
    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );

    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            if (blob) resolve(blob);
        }, 'image/jpeg', 0.9);
    });
};

const ProfileImageModal = ({
    isOpen,
    onClose,
    currentUser,
    onProfileImageUpdate,
    onUserInfoUpdate,
}: ProfileImageModalProps) => {
    const { getAccessToken } = useAuth();
    const { toast } = useToast();
    const { themeColor } = useColor();
    const APP_COLOR = appConfig.features.isDemoMode
        ? themeColor
        : appConfig.themeColor;
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Form state
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [dragActive, setDragActive] = useState(false);

    // Image cropping state
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
        x: number;
        y: number;
        width: number;
        height: number;
    } | null>(null);
    const [showCropper, setShowCropper] = useState(false);

    // Update form when currentUser changes
    useEffect(() => {
        if (currentUser) {
            setFirstName(currentUser.first_name || "");
            setLastName(currentUser.last_name || "");
        }
    }, [currentUser]);

    const onCropComplete = useCallback(
        (_croppedArea: any, croppedAreaPixels: any) => {
            setCroppedAreaPixels(croppedAreaPixels);
        },
        []
    );

    const handleFileSelect = (file: File) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast({
                title: "Ungültiger Dateityp",
                description: "Bitte wählen Sie eine Bilddatei aus.",
                variant: "destructive",
            });
            return;
        }

        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
            toast({
                title: "Datei zu groß",
                description: "Bitte wählen Sie ein Bild kleiner als 10MB aus.",
                variant: "destructive",
            });
            return;
        }

        // Create image URL for cropper
        const imageUrl = URL.createObjectURL(file);
        setImageSrc(imageUrl);
        setShowCropper(true);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
    };

    const handleCropSave = async () => {
        if (!imageSrc || !croppedAreaPixels) return;

        try {
            setIsUploading(true);

            // Get cropped image as blob
            const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);

            // Convert blob to file
            const croppedFile = new File([croppedImageBlob], 'profile-image.jpg', {
                type: 'image/jpeg'
            });

            // Upload cropped image
            const token = await getAccessToken();
            const response = await api.uploadProfileImage(croppedFile, token);

            onProfileImageUpdate(response.profile_image_url);

            toast({
                title: "Profilbild aktualisiert",
                description: "Ihr Profilbild wurde erfolgreich aktualisiert.",
            });

            // Reset cropper state
            setShowCropper(false);
            setImageSrc(null);
            URL.revokeObjectURL(imageSrc);
        } catch (error) {
            console.error("Failed to upload profile image:", error);
            toast({
                title: "Upload fehlgeschlagen",
                description: "Profilbild konnte nicht hochgeladen werden. Bitte versuchen Sie es erneut.",
                variant: "destructive",
            });
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleCropCancel = () => {
        if (imageSrc) {
            URL.revokeObjectURL(imageSrc);
        }
        setShowCropper(false);
        setImageSrc(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const handleUpdateProfile = async () => {
        if (!currentUser) return;

        try {
            setIsUpdating(true);
            const token = await getAccessToken();

            const updatedData = {
                first_name: firstName,
                last_name: lastName,
            };

            await api.updateCurrentUser(updatedData, token);

            const updatedUser = {
                ...currentUser,
                first_name: firstName,
                last_name: lastName,
            };

            onUserInfoUpdate(updatedUser);

            toast({
                title: "Profil aktualisiert",
                description: "Ihre Profilinformationen wurden erfolgreich aktualisiert.",
            });
        } catch (error) {
            console.error("Failed to update profile:", error);
            toast({
                title: "Aktualisierung fehlgeschlagen",
                description: "Profilinformationen konnten nicht aktualisiert werden. Bitte versuchen Sie es erneut.",
                variant: "destructive",
            });
        } finally {
            setIsUpdating(false);
        }
    };

    const getInitials = () => {
        if (!currentUser) return "U";
        const firstName = currentUser.first_name || "";
        const lastName = currentUser.last_name || "";
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "U";
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative">
                {showCropper ? (
                    // Image Cropper View
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold">Profilbild zuschneiden</h2>
                            <button
                                onClick={handleCropCancel}
                                className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-black hover:text-gray-700 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="relative w-full h-64 bg-gray-100 rounded-lg overflow-hidden mb-4">
                            <Cropper
                                image={imageSrc!}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                cropShape="round"
                                showGrid={false}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Zoom
                            </label>
                            <input
                                type="range"
                                min={1}
                                max={3}
                                step={0.1}
                                value={zoom}
                                onChange={(e) => setZoom(Number(e.target.value))}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={handleCropCancel}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-md bg-transparent hover:bg-slate-100 transition-colors"
                            >
                                Abbrechen
                            </button>
                            <button
                                onClick={handleCropSave}
                                disabled={isUploading}
                                className="flex-1 px-4 py-2 text-white rounded-md disabled:opacity-50 transition-colors"
                                style={{ backgroundColor: APP_COLOR }}
                            >
                                {isUploading ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Wird hochgeladen...
                                    </div>
                                ) : (
                                    "Bild speichern"
                                )}
                            </button>
                        </div>
                    </div>
                ) : (
                    // Profile Edit View
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold">Profil bearbeiten</h2>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-black hover:text-gray-700 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="flex justify-center mb-4">
                            <div
                                className={`relative group cursor-pointer ${dragActive ? 'ring-2 ring-blue-400' : ''
                                    }`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Avatar className="w-20 h-20 group-hover:ring-2 group-hover:ring-blue-400 transition-all">
                                    <AvatarImage src={currentUser?.profile_image_url || undefined} alt="Profile" />
                                    <AvatarFallback className="text-lg font-medium">
                                        {getInitials()}
                                    </AvatarFallback>
                                </Avatar>

                                {/* Upload Overlay */}
                                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Camera className="w-6 h-6 text-white" />
                                </div>

                                {/* Loading Overlay */}
                                {isUploading && (
                                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <p className="text-xs text-gray-500 text-center mb-4">
                            Auf Foto klicken zum Ändern
                        </p>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileInput}
                            className="hidden"
                        />

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Vorname
                                </label>
                                <input
                                    type="text"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    placeholder="Geben Sie Ihren Vornamen ein"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nachname
                                </label>
                                <input
                                    type="text"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    placeholder="Geben Sie Ihren Nachnamen ein"
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={onClose}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-md bg-transparent hover:bg-slate-100 transition-colors"
                            >
                                Schließen
                            </button>
                            <button
                                onClick={handleUpdateProfile}
                                disabled={isUpdating}
                                className="flex-1 px-4 py-2 text-white rounded-md disabled:opacity-50 transition-colors"
                                style={{
                                    backgroundColor: appConfig.themeColor,
                                    borderColor: appConfig.themeColor
                                }}
                                onMouseEnter={(e) => {
                                    if (!isUpdating) {
                                        e.currentTarget.style.backgroundColor = appConfig.themeColor;
                                        e.currentTarget.style.color = 'white';
                                        e.currentTarget.style.borderColor = '#3b82f6';
                                        e.currentTarget.style.borderWidth = '1px';
                                        e.currentTarget.style.filter = 'brightness(0.8)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isUpdating) {
                                        e.currentTarget.style.backgroundColor = appConfig.themeColor;
                                        e.currentTarget.style.color = 'white';
                                        e.currentTarget.style.borderColor = appConfig.themeColor;
                                        e.currentTarget.style.borderWidth = '1px';
                                        e.currentTarget.style.filter = 'none';
                                    }
                                }}
                            >
                                {isUpdating ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Wird aktualisiert...
                                    </div>
                                ) : (
                                    "Aktualisieren"
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProfileImageModal; 